import { buffers } from 'redux-saga';
import { call, all, put, fork, select, take, takeEvery, actionChannel, delay } from 'redux-saga/effects';
import { browserHistory } from 'react-router';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import filter from 'lodash/filter';
import reduce from 'lodash/reduce';

import * as c from '../constants';
import * as selectors from '../selectors';
import * as utils from '../utils';
import * as actions from '../actions';
import * as api from '../services';
import * as DocType from '../../../../constants/constant_doc_types';
import { saveTabAndChildren, saveTemplate } from './sagas_save';




export default function* (feathersApp) {
  yield all([
    fork(selectHotkeyControllerSaga, feathersApp),

    fork(saveTemplateSaga, feathersApp),
    fork(editTemplateSaga, feathersApp),
    fork(deleteTemplateSaga, feathersApp),

    fork(moveTabSaga, feathersApp),
    fork(moveChildSaga, feathersApp),
    fork(addNewTabSaga, feathersApp),
    fork(addNewChildSaga, feathersApp),
    fork(editTabSaga, feathersApp),
    fork(editChildSaga, feathersApp),
    fork(deleteTabSaga, feathersApp),
    fork(deleteChildSaga, feathersApp),
    fork(toggleTabActiveSaga, feathersApp),
    fork(toggleChildActiveSaga, feathersApp),
  ]);
}




function* saveTemplateSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_SAVE_HOTKEY_TEMPLATE, callSaveTemplate, feathersApp);
}
function* callSaveTemplate(feathersApp, action) {
  try {
    yield put(actions.setTemplateIsSaving());

    const { payload: data } = action;
    const tabList = yield select(selectors.getTabList);

    const template = yield call(saveTemplate, feathersApp, data) || {};
    const { id, reference_id } = template;

    if (!reference_id) {
      throw new Error("Failed to generate reference id.");
    }

    for (let level in tabList) {
      if (yield select(selectors.getLevelSaveStatus, level)) {
        continue;
      }

      const { items: tabs } = tabList[level];

      for (let tab of tabs) {
        yield call(saveTabAndChildren, feathersApp, tab);
      }
    }

    yield put(actions.setTemplateState(template));
    yield put(actions.setTemplateDoneSaving());
    yield put(actions.resetLevelSave());

    forwardTo(`/admin/hotkey/template/edit/${id}`);

  } catch(err) {
    console.error('@callSaveNewTemplate: ', err.message || err);
    throw err;
  }
}




function* editTemplateSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_EDIT_HOTKEY_TEMPLATE, callEditTemplate, feathersApp);
}
function* callEditTemplate(feathersApp, action) {
  try {
    yield put(actions.setTemplateIsFetching());

    const { payload: id } = action;
    const template = yield call(api.getTemplateById, feathersApp, id);

    if (!template) {
      yield put(actions.setTemplateError('Template not found.'));
      return;
    }

    yield put(actions.setTemplateState(template));
    yield put(actions.selectHome());

  } catch(err) {
    console.error('@callEditTemplate: ', err.message || err);
    yield put(actions.setTemplateError(err.message));
  }
}




function* deleteTemplateSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_DELETE_HOTKEY_TEMPLATE, callDeleteTemplate, feathersApp);
}
function* callDeleteTemplate(feathersApp, action) {
  yield put({ type: 'DELETE_RECORD_INPROGRESS' });
  try {
    yield put(actions.setTemplateIsSaving());

    const { reroute, payload: id } = action;

    yield call(api.deleteTemplateById, feathersApp, id);
    yield put(actions.setTemplateDoneSaving());
    yield put({ type: "DELETE_RECORD_DONE", payload: { id, docType: DocType.HOTKEY_TEMPLATE } });
    yield delay(2000);
    reroute && browserHistory.push(reroute);

  } catch(error) {
    yield put({ type: 'DELETE_RECORD_ERROR', error });
    yield put(actions.setTemplateDoneSaving());
  }
}




function* selectHotkeyControllerSaga(feathersApp) {
  yield queueEvery(c.HOTKEY_TMP_SELECT_HOTKEY, callSelectHotkeyController, feathersApp);
}
function* callSelectHotkeyController(feathersApp, action) {
  try {
    const { mode, payload: hotkey } = action;
    const { is_group, active } = hotkey || {};

    if (!hotkey) {
      return yield call(selectHome, feathersApp, action);
    }

    if (mode !== c.SELECT_MODE_BREADCRUMB && (!is_group || !active)) {
      return;
    }

    switch (mode) {
      case c.SELECT_MODE_TAB: {
        return yield call(selectTab, feathersApp, action);
      }

      case c.SELECT_MODE_CHILD: {
        return yield call(selectChild, feathersApp, action);
      }

      case c.SELECT_MODE_BREADCRUMB: {
        return yield call(selectBreadcrumb, feathersApp, action);
      }

      default: {
        return;
      }
    }
  } catch(err) {
    console.error('@callSelectHotkeyController: ', err.message || err);
    throw err;
  }
}




function* selectHome(feathersApp, action) {
  try {
    yield put(actions.setTemplateIsFetching());

    const { id: templateId } = yield select(selectors.getTemplateState);

    if (!isEmpty(yield select(selectors.getTabsAtLevel, 0))) {
      yield put(actions.setLevel(0));
      yield put(actions.setSelectedTab(0));
      yield put(actions.setTemplateDoneFetching());
      return;
    }

    const templateTabs = yield call(api.getTemplateRootTabs, feathersApp, templateId);
    const hotkeyTabs = yield call(api.getHotkeyRootTabs, feathersApp);

    const rootTabs = utils.resolveHotkeyGroupByTemplateGroup(hotkeyTabs, templateTabs);

    const tabs = utils.assembleHotkeyTemplate(rootTabs, null, Boolean(templateId));

    yield put(actions.setTabs(0, tabs));
    yield put(actions.setTemplateDoneFetching());

  } catch(err) {
    console.error('@selectHome: ', err.message || err);
    throw err;
  }
}




function* selectTab(feathersApp, action) {
  try {
    const { payload: tab } = action;
    const { is_default, is_class, key_level } = tab;

    const level = yield select(selectors.getTabLevel);
    const { id: templateId } = yield select(selectors.getTemplateState);

    yield put(actions.setSelectedTab(level, tab));

    // If the children has been assembled before, just show them
    if (!isEmpty(yield select(selectors.getTabChildren, key_level))) {
      return;
    }

    // Show loader
    yield put(actions.getChildren(key_level));

    // Query templates & hotkeys
    const templateChildren = (!is_default)
      ? yield call(api.getTemplateChildren, feathersApp, tab)
      : [];
    const templateChildrenGroup = yield call(api.getTemplateChildrenGroup, feathersApp, tab, templateId);

    const hotkeyChildren = (is_class)
      ? yield call(api.getHotkeyChildrenByClass, feathersApp, tab)
      : yield call(api.getHotkeyChildrenByItem, feathersApp, tab);
    const hotkeyChildrenGroup = yield call(api.getHotkeyChildrenGroup, feathersApp, tab);

    // Resolve and assemble children
    const children = utils.resolveHotkeyByTemplate(hotkeyChildren, hotkeyChildrenGroup, templateChildren, templateChildrenGroup);
    const nextChildren = utils.assembleHotkeyTemplate(children, tab, Boolean(templateId));

    yield put(actions.setInitialChildren(key_level, nextChildren));

  } catch(err) {
    console.error('@selectTab: ', err.message || err);
    throw err;
  }
}




function* selectChild(feathersApp, action) {
  try {
    const { payload: child } = action;
    const { level } = child;

    const tabKey = yield select(selectors.getSelectedTabKey);
    const nextTabs = yield select(selectors.getTabChildrenGroup, tabKey);

    yield put(actions.setLevel(level));
    yield put(actions.setTabs(level, nextTabs));
    yield put(actions.selectTab(child));

  } catch(err) {
    console.error('@selectChild: ', err.message || err);
    throw err;
  }
}




function* selectBreadcrumb(feathersApp, action) {
  try {
    const { payload: breadcrumb } = action;
    const { level, key } = breadcrumb;

    const tabs = yield select(selectors.getTabsAtLevel, level);
    const selected = find(tabs, { key });

    yield put(actions.setLevel(level));
    yield put(actions.selectTab(selected));

  } catch(err) {
    console.error('@selectBreadcrumb: ', err.message || err);
    throw err;
  }
}




function* moveTabSaga(feathersApp) {
  yield queueEvery(c.HOTKEY_TMP_MOVE_TAB, callMoveTab, feathersApp);
}
function* callMoveTab(feathersApp, action) {
  try {
    const { payload: { from, to } } = action;

    const level = yield select(selectors.getTabLevel);
    const parent = yield select(selectors.getParentForLevel, level);
    const { key_level: parentKey } = parent || {};

    const siblings = (!parentKey)
      ? yield select(selectors.getTabsAtLevel, 0)
      : yield select(selectors.getTabChildren, parentKey);

    const nextSiblings = utils.moveHotkey(siblings, from, to);
    const nextTabs = utils.getGroupFrom(nextSiblings);

    // Setup the children reducer of parent
    (parentKey) && (yield put(actions.setChildren(parentKey, nextSiblings)));

    // Setup the tab reducer of level
    yield put(actions.setTabs(level, nextTabs));

  } catch(err) {
    console.error('@callMoveTab: ', err.message || err);
    throw err;
  }
}




function* moveChildSaga(feathersApp) {
  yield queueEvery(c.HOTKEY_TMP_MOVE_CHILD, callMoveChild, feathersApp);
}
function* callMoveChild(feathersApp, action) {
  try {
    const tabKey = yield select(selectors.getSelectedTabKey);
    const children = yield select(selectors.getTabChildren, tabKey);
    const { payload: { from, to } } = action;

    const nextChildren = utils.moveHotkey(children, from, to);

    yield put(actions.setChildren(tabKey, nextChildren));

  } catch(err) {
    console.error('@callMoveChild: ', err.message || err);
    throw err;
  }
}




function* toggleTabActiveSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_TOGGLE_TAB_ACTIVE, callToggleTabActive, feathersApp);
}
function* callToggleTabActive(feathersApp, action) {
  try {
    const { payload: tab } = action;

    const level = yield select(selectors.getTabLevel);
    const parent = yield select(selectors.getParentForLevel, level);
    const { key_level: parentKey } = parent || {};

    const siblings = (!parentKey)
      ? yield select(selectors.getSortedTabsAtLevel, 0)
      : yield select(selectors.getTabSortedChildren, parentKey);

    const nextSiblings = utils.toggleHotkeyActive(siblings, tab);
    const nextTabs = utils.getGroupFrom(nextSiblings);

    // Setup the children reducer of parent
    (parentKey) && (yield put(actions.setChildren(parentKey, nextSiblings)));

    // Setup the tab reducer of level
    yield put(actions.setTabs(level, nextTabs));

    // Setup the selected tab
    const { active, key_level } = tab;
    const tabKey = yield select(selectors.getSelectedTabKey);

    if (tabKey === key_level && active) {
      yield put(actions.setSelectedTab(level));
    }

  } catch(err) {
    console.error('@callToggleTabActive: ', err.message || err);
    throw err;
  }
}




function* toggleChildActiveSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_TOGGLE_CHILD_ACTIVE, callToggleChildActive, feathersApp);
}
function* callToggleChildActive(feathersApp, action) {
  try {
    const tabKey = yield select(selectors.getSelectedTabKey);
    const children = yield select(selectors.getTabSortedChildren, tabKey);
    const { payload: child } = action;

    const nextChildren = utils.toggleHotkeyActive(children, child);

    yield put(actions.setChildren(tabKey, nextChildren));

  } catch(err) {
    console.error('@callToggleChildActive: ', err.message || err);
    throw err;
  }
}




function* addNewTabSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_ADD_NEW_TAB, callAddNewTab, feathersApp);
}
function* callAddNewTab(feathersApp, action) {
  try {
    const { payload: data } = action;
    const { group_type } = data;

    const { level, tabs } = yield select(selectors.getLevelAndTabs);
    const parent = yield select(selectors.getParentForLevel, level);

    const newTab = (group_type === c.FORM_GROUP_TYPE_ITEM)
      ? yield call(buildNewGroupItem, feathersApp, data, tabs.length, parent)
      : utils.buildNewGroupClass(data, tabs.length, parent);

    yield call(addTabToLevel, level, newTab);
    yield call(addChildrenToParent, parent, newTab);

  } catch(err) {
    console.error('@callAddNewTab: ', err.message || err);
    throw err;
  }
}




function* addNewChildSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_ADD_NEW_CHILD, callAddNewChild, feathersApp);
}
function* callAddNewChild(feathersApp, action) {
  try {
    const { payload: data } = action;

    const parent = yield select(selectors.getSelectedTab);
    const newChildren = yield call(buildNewChildren, feathersApp, data);

    yield call(addChildrenToParent, parent, newChildren);

  } catch(err) {
    console.error('@callAddNewChild: ', err.message || err);
    throw err;
  }
}




function* editTabSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_EDIT_TAB, callEditTab, feathersApp);
}
function* callEditTab(feathersApp, action) {
  try {
    const { payload: tab } = action;

    const level = yield select(selectors.getTabLevel);
    const parent = yield select(selectors.getParentForLevel, level);
    const { key_level: parentKey } = parent || {};

    const siblings = (!parentKey)
      ? yield select(selectors.getSortedTabsAtLevel, 0)
      : yield select(selectors.getTabSortedChildren, parentKey);

    const nextSiblings = utils.replaceHotkey(siblings, tab);
    const nextTabs = utils.getGroupFrom(nextSiblings);

    // Setup the children reducer of parent
    (parentKey) && (yield put(actions.setChildren(parentKey, nextSiblings)));

    // Setup the tab reducer of level
    yield put(actions.setTabs(level, nextTabs));

  } catch(err) {
    console.error('@callEditTab: ', err.message || err);
    throw err;
  }
}




function* editChildSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_EDIT_CHILD, callEditChild, feathersApp);
}
function* callEditChild(feathersApp, action) {
  try {
    const tabKey = yield select(selectors.getSelectedTabKey);
    const children = yield select(selectors.getTabSortedChildren, tabKey);
    const { payload: child } = action;

    const nextChildren = utils.replaceHotkey(children, child);

    yield put(actions.setChildren(tabKey, nextChildren));

  } catch(err) {
    console.error('@callEditChild: ', err.message || err);
    throw err;
  }
}




function* deleteTabSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_DELETE_TAB, callDeleteTab, feathersApp)
}
function* callDeleteTab(feathersApp, action) {
  try {
    const { payload: tab } = action;
    const { level, key_level: tabKey } = tab;

    yield call(recursiveDeleteGroup, tab);

    // Update selected tab
    const selectedKey = yield select(selectors.getSelectedTabKey);

    if (tabKey === selectedKey) {
      yield put(actions.setSelectedTab(level));
    }

  } catch(err) {
    console.error('@callDeleteTab: ', err.message || err);
    throw err;
  }
}




function* deleteChildSaga(feathersApp) {
  yield takeEvery(c.HOTKEY_TMP_DELETE_CHILD, callDeleteChild, feathersApp);
}
function* callDeleteChild(feathersApp, action) {
  try {
    const { payload: child } = action;
    const parentKey = yield select(selectors.getSelectedTabKey);

    const { is_group } = child;

    if (!is_group) {
      const children = yield select(selectors.getTabChildren, parentKey);
      const nextChildren = utils.deleteHotkeyPerm(children, child);

      return yield put(actions.setChildren(parentKey, nextChildren));
    }

    return yield call(recursiveDeleteGroup, child);

  } catch(err) {
    console.error('@callDeleteChild: ', err.message || err);
    throw err;
  }
}




//
// ─── UTILITY FUNCTIONS ──────────────────────────────────────────────────────────
//




function* addChildrenToParent(parent, newChildren) {
  if (isEmpty(parent)) {
    return;
  }

  const { key_level: parentKey } = parent;
  const children = yield select(selectors.getTabSortedChildren, parentKey);
  const nextChildren = (Array.isArray(newChildren))
    ? utils.insertHotkeys(children, ...newChildren)
    : utils.insertHotkeys(children, newChildren);

  yield put(actions.setChildren(parentKey, nextChildren));
}




function* recursiveDeleteGroup(group) {
  try {
    const { key_level, level, parent } = group;

    const children = yield select(selectors.getTabChildrenGroup, key_level);

    for (let cg of children) {
      const { deleted: cgDeleted, key: cgKey } = cg;

      if (
        !cgDeleted
        && cgKey !== c.ADD_DOC_ID
      ) {
        yield call(recursiveDeleteGroup, cg)
      }
    }

    const { id: groupId } = group;
    const parentKey = (parent || {}).key
    const parentKeyLevel = `${level - 1}::${parentKey}`;

    const siblings = (!parentKey)
      ? yield select(selectors.getTabsAtLevel, 0)
      : yield select(selectors.getTabChildren, parentKeyLevel);

    const nextSiblings = (!groupId)
      ? utils.deleteHotkeyPerm(siblings, group)
      : utils.deleteHotkeyMark(siblings, group);

    const nextTabs = filter(nextSiblings, { is_group: true });

    // Setup the children reducer of parent
    (parentKey) && (yield put(actions.setChildren(parentKeyLevel, nextSiblings)));

    // Setup the children reducer of this group
    (!groupId) && (yield put(actions.setChildren(key_level, [])));

    // Setup the tabs reducer of this group
    yield put(actions.setTabs(level, nextTabs));

  } catch(err) {
    console.error('@recursiveDeleteGroup: ', err.message || err);
    throw err;
  }
}




function* addTabToLevel(level = -1, newTab) {
  if (level < 0) {
    return;
  }

  const tabs = yield select(selectors.getSortedTabsAtLevel, level);
  const nextTabs = utils.insertHotkeys(tabs, newTab);

  yield put(actions.setTabs(level, nextTabs));
}




function* buildNewGroupItem(feathersApp, data, position, parent) {
  const { list: listIds } = data;
  const items = yield call(api.getItemByIds, feathersApp, listIds);
  const keyedItems = keyBy(items, 'id');

  const list = reduce(listIds, (res, id) => {
    const details = keyedItems[id];
    const { name, image_url } = details || {};

    return (!details)
      ? res
      : res.concat({
        id,
        name,
        image_url,
        active: true,
      });
  }, []);

  return utils.buildNewGroupItem({...data, list}, position, parent);
}




function* buildNewChildren(feathersApp, data) {
  try {
    const { add_type, group_type, list } = data;

    const tab = yield select(selectors.getSelectedTab);
    const { key_level: tabKeyLevel } = tab;
    const children = yield select(selectors.getTabChildren, tabKeyLevel);

    if (add_type === c.FORM_ADD_TYPE_GROUP) {
      return (group_type === c.FORM_GROUP_TYPE_ITEM)
        ? yield call(buildNewGroupItem, feathersApp, data, children.length, tab)
        : utils.buildNewGroupClass(data, children.length, tab)
    }

    const items = yield call(api.getItemByIds, feathersApp, list);
    const keyedItems = keyBy(items, 'id');
    const detailed = (list || []).map(id => {
      const { name = "", image_url = "" } = keyedItems[id] || {};
      return {
        id,
        name,
        image_url
      }
    });

    return utils.buildNewItems(detailed, children.length, tab);

  } catch(err) {
    console.error('@buildNewChildren: ', err.message || err);
    throw err;
  }
}




function queueEvery(pattern, saga, ...args) {
  return fork(
    function* () {
      const channel = yield actionChannel(pattern, buffers.expanding());
      while (true) {
        const action = yield take(channel);
        yield call(saga, ...args, action);
      }
    }
  );
}




function forwardTo(path) {
  window.location = path;
}
